// Story 1.30 — TanStack Query mutation: POST /api/v1/admin/bulletin/posts/:id/publish.

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";
import type { BulletinPostListItem } from "./useAdminBulletinList";

export type PublishBulletinInput = { id: string };

export type PublishBulletinResponse = {
  data: BulletinPostListItem;
};

async function publishPost(
  input: PublishBulletinInput
): Promise<PublishBulletinResponse> {
  const idempotencyKey = crypto.randomUUID();
  const response = await fetch(
    `/api/v1/admin/bulletin/posts/${input.id}/publish`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        "content-type": "application/json",
        "x-idempotency-key": idempotencyKey,
      },
      body: "{}",
    }
  );
  attachRequestIdToScope(response);
  if (!response.ok) {
    let message = `Publish bulletin failed (HTTP ${response.status})`;
    let code: string | undefined;
    try {
      const raw = await response.json();
      if (raw?.error?.message) message = raw.error.message;
      code = raw?.error?.code;
    } catch {
      // body not JSON
    }
    const err = new Error(message) as Error & { code?: string };
    err.code = code;
    throw err;
  }
  return (await response.json()) as PublishBulletinResponse;
}

export function usePublishBulletinPost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: publishPost,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["admin-bulletin-list"] });
      qc.invalidateQueries({ queryKey: ["admin-bulletin-detail", vars.id] });
    },
  });
}
